import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toPng } from 'html-to-image';
import {
  Container,
  FormSection,
  FormTitle,
  FormSubtitle,
  InputGroup,
  Label,
  Input,
  PhotoUploadArea,
  PhotoCircleEdit,
  PhotoPreviewImg,
  PhotoHint,
  PhotoPlaceholder,
  PhotoControls,
  ZoomControl,
  BtnPrimary,
  BtnSecondary,
  PreviewContainer,
  CardWrapper,
  Card,
  CardContent,
  CardLabel,
  CardTitle,
  CardTech,
  CardPhotoRing,
  CardPhotoInner,
  CardName,
  CardBadge,
  CardDivider,
  CardInfo,
  CardInfoItem,
  CardFooter,
  PreviewActions,
} from './styles';

export default function Welcome() {
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPosition, setPhotoPosition] = useState({ x: 0, y: 0 });
  const [photoScale, setPhotoScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const cardRef = useRef(null);
  const fileInputRef = useRef(null);
  const circleRef = useRef(null);
  const lastTouchDistance = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Fix iOS EXIF orientation by drawing to canvas
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const fixedDataUrl = canvas.toDataURL('image/jpeg', 0.92);

        setPhoto(fixedDataUrl);
        setPhotoPosition({ x: 0, y: 0 });
        setPhotoScale(1);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleMouseDown = (e) => {
    if (!photo) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - photoPosition.x,
      y: e.clientY - photoPosition.y,
    });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;
      setPhotoPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (!photo) return;
    if (e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - photoPosition.x,
        y: e.touches[0].clientY - photoPosition.y,
      });
    }
    if (e.touches.length === 2) {
      lastTouchDistance.current = getTouchDistance(e.touches);
    }
  };

  const handleTouchMove = useCallback(
    (e) => {
      if (!photo) return;
      e.preventDefault();
      if (e.touches.length === 1 && isDragging) {
        setPhotoPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
      if (e.touches.length === 2) {
        const dist = getTouchDistance(e.touches);
        if (lastTouchDistance.current) {
          const delta = dist / lastTouchDistance.current;
          setPhotoScale((prev) => Math.min(Math.max(prev * delta, 0.3), 5));
        }
        lastTouchDistance.current = dist;
      }
    },
    [isDragging, dragStart, photo]
  );

  const handleTouchEnd = () => {
    setIsDragging(false);
    lastTouchDistance.current = null;
  };

  const handleWheel = (e) => {
    if (!photo) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.95 : 1.05;
    setPhotoScale((prev) => Math.min(Math.max(prev * delta, 0.3), 5));
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useEffect(() => {
    const el = circleRef.current;
    if (!el) return;
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', handleTouchMove);
  }, [handleTouchMove]);

  const handleGenerate = () => {
    if (!name.trim()) {
      alert('Por favor, digite seu nome.');
      return;
    }
    if (!photo) {
      alert('Por favor, selecione uma foto.');
      return;
    }
    setShowPreview(true);
  };

  const handleSave = async () => {
    if (!cardRef.current) return;
    setSaving(true);

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Open window SYNCHRONOUSLY (before any await) to avoid Safari popup blocker
    let newTab = null;
    if (isIOS) {
      newTab = window.open('about:blank');
      if (newTab) {
        newTab.document.write(
          '<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><title>Gerando...</title><style>body{margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;background:#111;color:#fff;font-family:sans-serif}</style></head><body><p>Gerando imagem...</p></body></html>'
        );
        newTab.document.close();
      }
    }

    try {
      await new Promise((r) => setTimeout(r, 500));

      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 3,
        width: 720,
        height: 1280,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left',
          width: '720px',
          height: '1280px',
        },
      });

      if (isIOS && newTab) {
        newTab.document.open();
        newTab.document.write(
          `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><title>Salvar Imagem</title><style>body{margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;background:#111;color:#fff;font-family:sans-serif}img{max-width:90vw;max-height:80vh;border-radius:8px}p{margin-top:16px;font-size:14px;opacity:0.7}</style></head><body><img src="${dataUrl}"/><p>Segure na imagem para salvar na galeria</p></body></html>`
        );
        newTab.document.close();
      } else if (!isIOS) {
        const link = document.createElement('a');
        link.download = `confirmacao-${name.trim()}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      console.error('Erro ao salvar:', err);
      if (newTab) newTab.close();
      alert('Erro ao gerar imagem. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (showPreview) {
    return (
      <Container>
        <PreviewContainer>
          <CardWrapper>
            <Card ref={cardRef}>
              <CardContent>
                <CardLabel>WORKSHOP</CardLabel>
                <CardTitle>
                  Construa em 2 horas o sistema de recomendacao de conteudo do
                  Instagram.
                </CardTitle>
                <CardTech>EMBEDDINGS + LARAVEL 13 + IA + LIVEWIRE</CardTech>

                <CardPhotoRing>
                  <CardPhotoInner>
                    {photo && (
                      <img
                        src={photo}
                        alt="Foto"
                        draggable={false}
                        style={{
                          transform: `translate(${photoPosition.x}px, ${photoPosition.y}px) scale(${photoScale})`,
                        }}
                      />
                    )}
                  </CardPhotoInner>
                </CardPhotoRing>

                <CardName>{name}</CardName>

                <CardBadge>
                  <span>PARTICIPANTE CONFIRMADO</span>
                </CardBadge>

                <CardDivider />

                <CardInfo>
                  <CardInfoItem>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#e05aad"
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                    <span>22 de abril</span>
                  </CardInfoItem>
                  <CardInfoItem>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#e05aad"
                      strokeWidth="2"
                    >
                      <path d="M15.6 11.6L22 7v10l-6.4-4.6v-1zM3 7h12a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V8a1 1 0 011-1z" />
                    </svg>
                    <span>Via Google Meet</span>
                  </CardInfoItem>
                </CardInfo>

                <CardFooter>BEER AND CODE - 2026</CardFooter>
              </CardContent>
            </Card>
          </CardWrapper>

          <PreviewActions>
            <BtnSecondary onClick={() => setShowPreview(false)}>
              Voltar e editar
            </BtnSecondary>
            <BtnPrimary onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar imagem'}
            </BtnPrimary>
          </PreviewActions>
        </PreviewContainer>
      </Container>
    );
  }

  return (
    <Container>
      <FormSection>
        <FormTitle>Confirme sua presenca</FormTitle>
        <FormSubtitle>Gere sua imagem de participante confirmado</FormSubtitle>

        <InputGroup>
          <Label>Seu nome</Label>
          <Input
            type="text"
            placeholder="Digite seu nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={30}
          />
        </InputGroup>

        <InputGroup>
          <Label>Sua foto</Label>
          <PhotoUploadArea
            ref={circleRef}
            onClick={!photo ? () => fileInputRef.current?.click() : undefined}
            onMouseDown={photo ? handleMouseDown : undefined}
            onWheel={photo ? handleWheel : undefined}
            onTouchStart={photo ? handleTouchStart : undefined}
            onTouchEnd={photo ? handleTouchEnd : undefined}
          >
            {photo ? (
              <>
                <PhotoCircleEdit>
                  <PhotoPreviewImg
                    src={photo}
                    alt="Foto"
                    draggable={false}
                    style={{
                      transform: `translate(${photoPosition.x}px, ${photoPosition.y}px) scale(${photoScale})`,
                    }}
                  />
                </PhotoCircleEdit>
                <PhotoHint>Arraste para mover | Scroll/Pinch para zoom</PhotoHint>
              </>
            ) : (
              <PhotoPlaceholder>
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
                <span>Toque para selecionar foto</span>
              </PhotoPlaceholder>
            )}
          </PhotoUploadArea>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {photo && (
            <PhotoControls>
              <BtnSecondary onClick={() => fileInputRef.current?.click()}>
                Trocar foto
              </BtnSecondary>
              <ZoomControl>
                <label>Zoom</label>
                <input
                  type="range"
                  min="0.3"
                  max="5"
                  step="0.05"
                  value={photoScale}
                  onChange={(e) => setPhotoScale(parseFloat(e.target.value))}
                />
              </ZoomControl>
            </PhotoControls>
          )}
        </InputGroup>

        <BtnPrimary onClick={handleGenerate}>Gerar imagem</BtnPrimary>
      </FormSection>
    </Container>
  );
}
